import axios from 'axios';
import { getRecord, saveRecord } from '@/services/localDb';

const API = () => `${process.env.VUE_APP_API_URL}/v1/api/tpv/qr-orders`;
const CURSOR_KEY = 'ewu_qr_delivery_cursor';
const QR_NOT_ENABLED_RETRY_MS = 5 * 60 * 1000;
const CAPABILITIES = {
  can_receive_qr_orders: true,
  can_persist_qr_receipts: true,
  can_display_orders: true,
  can_print_orders: false,
  cloud_websocket_connected: true,
  protocol_version: 1,
};

let lastTickAt = 0;
let nextTickAt = 0;

function uuid() {
  return typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-0000-4000-8000-${Math.random().toString(16).slice(2, 14)}`;
}

function cursor() {
  try { return Number(localStorage.getItem(CURSOR_KEY) || 0); } catch { return 0; }
}

function saveCursor(value) {
  try { localStorage.setItem(CURSOR_KEY, String(value || 0)); } catch { /* optional */ }
}

function headers(token, deviceKey) {
  return {
    Authorization: `Bearer ${token}`,
    'X-EWU-Device-Key': deviceKey,
  };
}

async function persistReceipt(delivery, deviceId) {
  const existing = await getRecord('qr_delivery_receipts', delivery.delivery_uuid);
  if (existing) return existing;
  const now = new Date().toISOString();
  const receipt = {
    receipt_uuid: uuid(),
    delivery_uuid: delivery.delivery_uuid,
    submission_uuid: delivery.submission_uuid,
    restaurant_id: delivery.payload?.restaurant_id || null,
    operation_epoch: delivery.operation_epoch,
    receiver_type: 'REGISTERED_DEVICE',
    receiver_id: deviceId,
    device_uuid: deviceId,
    delivery_version: delivery.delivery_version,
    payload_hash: delivery.payload_hash,
    received_at: now,
    persisted_at: now,
    processing_status: 'RECEIVED',
    display_status: 'PENDING_CLOUD_ORDER',
    print_status: 'NOT_REQUESTED',
    cloud_ack_status: 'PENDING',
    ack_uuid: uuid(),
    created_at: now,
    updated_at: now,
  };
  const saved = await saveRecord('qr_delivery_receipts', receipt);
  if (!saved) throw new Error('QR_RECEIPT_PERSIST_FAILED');
  return saved;
}

async function ackReceipt(receipt, token, deviceKey) {
  try {
    const response = await axios.post(
      `${API()}/deliveries/${receipt.delivery_uuid}/ack`,
      {
        ack_uuid: receipt.ack_uuid,
        submission_uuid: receipt.submission_uuid,
        delivery_version: receipt.delivery_version,
        operation_epoch: receipt.operation_epoch,
        receiver_type: 'REGISTERED_DEVICE',
        receiver_id: receipt.receiver_id,
        payload_hash: receipt.payload_hash,
        persisted_at: receipt.persisted_at,
        capabilities: {
          displayed: true,
          printing_available: false,
        },
      },
      { headers: headers(token, deviceKey), timeout: 8000 },
    );
    await saveRecord('qr_delivery_receipts', {
      ...receipt,
      cloud_ack_status: 'ACKNOWLEDGED',
      processing_status: 'CONFIRMED_IN_CLOUD',
      display_status: 'LOAD_FROM_CLOUD',
      order_id: response.data?.order_id || null,
      updated_at: new Date().toISOString(),
    });
    window.dispatchEvent(new CustomEvent('ewu-qr-order-confirmed', {
      detail: response.data,
    }));
  } catch (error) {
    const terminal = ['QR_ACK_TOO_LATE', 'QR_DELIVERY_EXPIRED', 'QR_DELIVERY_CANCELLED']
      .includes(error?.response?.data?.code);
    await saveRecord('qr_delivery_receipts', {
      ...receipt,
      cloud_ack_status: terminal ? 'REJECTED' : 'RETRY_PENDING',
      processing_status: terminal ? 'ARCHIVED' : receipt.processing_status,
      last_error: error?.response?.data?.code || error?.message,
      updated_at: new Date().toISOString(),
    });
    if (!terminal) throw error;
  }
}

export async function runQrReceiverTick({ token, deviceId, deviceKey, operationMode }) {
  const now = Date.now();
  if (!token || !deviceId || !deviceKey || operationMode !== 'CLOUD_MODE') return;
  if (now < nextTickAt || now - lastTickAt < 4500) return;
  lastTickAt = now;
  try {
    const leaseResponse = await axios.post(`${API()}/receivers/lease`, {
      capabilities: CAPABILITIES,
      last_delivery_cursor: cursor(),
    }, { headers: headers(token, deviceKey), timeout: 8000 });

    const deliveryBatch = leaseResponse.data?.delivery_batch;
    const responseData = deliveryBatch || (await axios.get(
      `${API()}/deliveries/pending`,
      {
        params: { after_cursor: cursor(), limit: 25 },
        headers: headers(token, deviceKey),
        timeout: 8000,
      },
    )).data;
    for (const delivery of responseData?.deliveries || []) {
      const receipt = await persistReceipt(delivery, deviceId);
      await ackReceipt(receipt, token, deviceKey);
    }
    saveCursor(responseData?.next_cursor ?? cursor());
    nextTickAt = now + Math.max(
      4500,
      Number(leaseResponse.data?.refresh_after_seconds || 5) * 1000,
    );
  } catch (error) {
    const code = error?.response?.data?.code;
    nextTickAt = now + (code === 'QR_NOT_ENABLED' ? QR_NOT_ENABLED_RETRY_MS : 8000);
  }
}
